<script setup lang="ts">
/**
 * Колонка доски — макет 9:2: шапка 28 (точка, название, счётчик, плюс) и
 * список карточек 264 в ширину.
 *
 * Кнопка «…» из шапки макета появится вместе со своим меню — DEV-1468. Пока
 * плюс стоит на её месте, с краю: кнопка, которая ничего не делает, дороже
 * отсутствующей — её нажимают.
 */
import { Plus } from 'lucide-vue-next'
import draggable from 'vuedraggable'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type { BoardColumn, BoardIssue } from '../api'
import BoardIssueCard from './BoardIssueCard.vue'

const props = defineProps<{
  column: BoardColumn
  /** Карточку тащат — пустая колонка показывает, что её здесь ждут */
  isDragging: boolean
  /** Задача, которую только что перенесли: карточка отвечает вспышкой */
  landedId?: string
  /** Колонку догружают: кнопка ждёт ответа и не жмётся второй раз */
  isLoadingMore?: boolean
}>()

const emit = defineEmits<{
  move: [issueId: string, columnId: string, beforeIssueId: string | undefined]
  create: [columnId: string]
  loadMore: [columnId: string]
  dragStart: []
  dragEnd: []
}>()

const { t } = useI18n()

// Цвет точки приходит с колонкой; у досок, заведённых до DEV-1464, его может
// не быть — тогда различаем хотя бы категорию.
const CATEGORY_COLORS: Record<string, string> = {
  STATUS_CATEGORY_OPEN: 'var(--color-status-todo)',
  STATUS_CATEGORY_IN_PROGRESS: 'var(--color-status-in-progress)',
  STATUS_CATEGORY_DONE: 'var(--color-status-done)',
}

const dotColor = computed(
  () =>
    props.column.color ||
    CATEGORY_COLORS[props.column.statusCategory] ||
    'var(--color-status-backlog)',
)

const hasLimit = computed(() => props.column.wipLimit > 0)

// Длина списка плюс необъехавший хвост: карточек в длинной колонке приезжает
// страница, а счётчик обязан считать все.
const count = computed(() => props.column.issues.length + props.column.hidden)

/** Лимит мягкий: перелив показывается, а не запрещается */
const isOverLimit = computed(() => hasLimit.value && count.value > props.column.wipLimit)

const counter = computed(() =>
  hasLimit.value ? `${count.value} / ${props.column.wipLimit}` : String(count.value),
)

interface ChangeEvent {
  added?: { element: BoardIssue; newIndex: number }
  moved?: { element: BoardIssue; newIndex: number }
}

/**
 * Карточку в списке уже переставил vuedraggable — отсюда уходит только адрес
 * переноса. Соседом считаем того, кто оказался следующим: сервер ставит задачу
 * перед ним, а пустой сосед означает конец колонки.
 */
const onChange = (event: ChangeEvent) => {
  const change = event.added ?? event.moved

  if (!change) return

  emit('move', change.element.id, props.column.id, props.column.issues[change.newIndex + 1]?.id)
}
</script>

<template>
  <section class="bcol" :class="{ 'bcol--dim': isDragging }">
    <header class="bcol__head">
      <span class="bcol__dot" :style="{ background: dotColor }" aria-hidden="true" />
      <h2 class="bcol__name" :title="column.name">{{ column.name }}</h2>
      <span class="bcol__count" :class="{ 'bcol__count--over': isOverLimit }">{{ counter }}</span>

      <button
        type="button"
        class="bcol__add-icon"
        :title="t('issues.create.action')"
        :aria-label="t('issues.create.action')"
        @click="emit('create', column.id)"
      >
        <Plus :size="15" />
      </button>
    </header>

    <!--
      `fallback-on-body` здесь — не украшение. Без него Sortable кладёт летящую
      копию карточки внутрь того же списка, а список прокручиваемый: копия
      обрезается краем своей колонки, и со стороны видно, что карточка ездит
      только по ней. Куда её на самом деле несут, при этом не видно вовсе.

      `bubble-scroll` даёт доске ехать вбок, когда карточку подносят к краю
      экрана: колонок пять и больше, и без прокрутки в дальнюю не донести.
    -->
    <draggable
      :list="column.issues"
      :group="{ name: 'board' }"
      item-key="id"
      class="bcol__list"
      ghost-class="icard--ghost"
      drag-class="icard--drag"
      :animation="180"
      :scroll-sensitivity="120"
      :scroll-speed="16"
      :force-fallback="true"
      :fallback-on-body="true"
      :fallback-tolerance="4"
      :bubble-scroll="true"
      @change="onChange"
      @start="emit('dragStart')"
      @end="emit('dragEnd')"
    >
      <template #item="{ element }">
        <BoardIssueCard :issue="element" :landed="element.id === landedId" />
      </template>
    </draggable>

    <!--
      Хвост длинной колонки. Кнопка стоит над «создать задачу», потому что
      относится к списку выше: без неё счётчик в шапке показывает сотню там,
      где на экране полсотни карточек, и свежие — те, что встают в конец
      колонки, — не видны вовсе (DEV-1640).
    -->
    <button
      v-if="column.hidden > 0"
      type="button"
      class="bcol__more"
      :disabled="isLoadingMore"
      @click="emit('loadMore', column.id)"
    >
      {{ isLoadingMore ? t('board.loadingMore') : t('board.loadMore', { count: column.hidden }) }}
    </button>

    <button type="button" class="bcol__add" @click="emit('create', column.id)">
      <Plus :size="14" />
      {{ t('issues.create.inColumn') }}
    </button>

    <p v-if="isDragging && !column.issues.length" class="bcol__drop">
      {{ t('board.dropHere') }}
    </p>
  </section>
</template>

<style scoped>
.bcol {
  position: relative;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 280px;
  max-height: 100%;
  min-height: 0;
  padding: 8px;
}

/* На телефоне колонка одна, а её статус назван пилюлей сверху — вторая
   подпись подряд только съедает высоту (макет 27:2). */
@media (max-width: 768px) {
  .bcol__head {
    display: none;
  }
}

.bcol__head {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  height: 28px;
  padding: 0 6px;
}

.bcol__dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: var(--radius-full);
}

.bcol__name {
  margin: 0;
  overflow: hidden;
  color: var(--color-text-secondary);
  font-size: var(--font-size-overline);
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.06em;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
}

.bcol__count {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  height: 20px;
  padding: 0 6px;
  border-radius: var(--radius-sm);
  color: var(--color-text-muted);
  font-size: var(--font-size-overline);
  font-variant-numeric: tabular-nums;
}

/* Перелив мягкого лимита: колонку не запирают, но и молчать о ней нельзя. */
.bcol__count--over {
  background: var(--color-warning-tint);
  color: var(--color-warning-strong);
}

.bcol__list {
  /* Единственная скроллящаяся часть колонки: шапка и «Задача» остаются на
     месте. Растёт по карточкам, а не на всю высоту — иначе кнопка уезжала бы
     в самый низ колонки вместо места сразу под последней карточкой. */
  display: flex;
  flex-direction: column;
  flex: 0 1 auto;
  gap: 8px;
  min-height: 40px;
  /* Полоса прокрутки ложится в правый отступ колонки, а не отъедает ширину у
     карточек: в макете карточка ровно 264 — и в короткой колонке, и в длинной.
     Место под неё держится всегда (stable), иначе карточки дёргались бы на
     семь пикселей при появлении прокрутки. */
  margin-right: -7px;
  padding-top: 8px;
  overflow-y: auto;
  scrollbar-gutter: stable;
}

.bcol__list::-webkit-scrollbar {
  width: 7px;
}

.bcol__list::-webkit-scrollbar-thumb {
  border-radius: var(--radius-full);
  background: var(--color-border-strong);
}

/* Плюс в шапке и «Задача» под карточками — две точки входа из макета: одна
   под рукой у названия колонки, другая там, где взгляд остановился, дочитав
   карточки. */
.bcol__add-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-left: auto;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-muted);
  cursor: pointer;
}

.bcol__add-icon:hover {
  color: var(--color-primary);
}

.bcol__add {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 6px;
  height: 32px;
  margin-top: 8px;
  padding: 0 10px;
  border: none;
  border-radius: var(--radius-md);
  background: none;
  color: var(--color-text-muted);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body);
  cursor: pointer;
}

.bcol__add:hover {
  background: var(--color-bg-muted);
  color: var(--color-text-primary);
}

.bcol__more {
  flex-shrink: 0;
  height: 28px;
  margin-top: 8px;
  padding: 0 10px;
  border: none;
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-caption);
  cursor: pointer;
}

.bcol__more:hover:not(:disabled) {
  color: var(--color-text-primary);
}

.bcol__more:disabled {
  color: var(--color-text-disabled);
  cursor: default;
}

/* Приглашение стоит ровно там, куда ляжет карточка — на месте первой в
   колонке, а не внизу пустого списка. Кликов не ловит: под ним зона приёма
   Sortable, и перехваченный курсор означал бы «сюда нельзя». */
.bcol__drop {
  position: absolute;
  top: 44px;
  right: 8px;
  left: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 92px;
  margin: 0;
  border: 1px dashed var(--color-primary);
  border-radius: var(--radius-md);
  background: var(--color-primary-tint);
  color: var(--color-primary);
  font-size: var(--font-size-body-sm);
  pointer-events: none;
}

/* Колонка, куда сейчас ляжет карточка: след Sortable уже переехал в её список,
   и колонка отвечает — это единственный честный признак приёмника, потому что
   курсор в это время держит копию, летящую над всей доской. */
.bcol:has(.icard--ghost) .bcol__list {
  border-radius: var(--radius-md);
  outline: 1px dashed var(--color-primary);
  outline-offset: 4px;
}

/* Пока карточку несут, колонки притухают — кроме той, куда она ляжет. Взгляд
   остаётся на цели, а не разбегается по всей доске. */
.bcol--dim {
  opacity: 0.7;
  transition: opacity 0.15s ease;
}

.bcol--dim:has(.icard--ghost) {
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .bcol--dim {
    opacity: 1;
  }
}
</style>
